/* eslint-disable max-lines-per-function */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  Outlet,
  OutletUpdateInput
} from "@bluframe/mightychroma-mightytool-frontend-types"
import slugify from "@sindresorhus/slugify"
import useItemView, { UseItemViewReturn } from "hooks/useListItemView"
import { FormEventHandler, RefObject, useCallback } from "react"

import Item from "./Item"

export interface Props {
  isAdd?: boolean
  isAdmin?: boolean
  isEditing?: boolean
  isEditor?: boolean
  isSaveLoading?: boolean
  item?: Outlet
  onCancel: () => void
  onDelete?: () => void
  onEdit?: () => void
  onPublishToggle?: () => void

  onSave: (input: OutletUpdateInput) => void
}

export interface ComponentProps
  extends
    Omit<
      Props,
      | "isEditing"
      | "isSaveLoading"
      | "onCancel"
      | "onDelete"
      | "onEdit"
      | "onPublishToggle"
      | "onSave"
    >,
    Omit<UseItemViewReturn, "item" | "refs"> {
  bioRef: RefObject<HTMLTextAreaElement | null>
  domainRef: RefObject<HTMLInputElement | null>
  isActionButtonLoading?: boolean
  logoImageIdRef: RefObject<HTMLInputElement | null>
  nameRef: RefObject<HTMLInputElement | null>
  onGenerateSlug: () => void
  onSubmit: FormEventHandler
  slugRef: RefObject<HTMLInputElement | null>
  splashImageIdRef: RefObject<HTMLInputElement | null>
  urlRef: RefObject<HTMLInputElement | null>
}

const usePrepareComponent = ({
  isAdd,
  isEditing,
  isSaveLoading,
  item,
  onCancel,
  onDelete,
  onEdit,
  onPublishToggle,
  onSave,
  ...props
}: Props): ComponentProps => {
  const {
    actionButtonOnClick,
    actionButtonLabel,
    isActionButtonLoading,
    isEditMode,
    isModalOpen,
    modalContent,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    outlinedButtonLabel,
    outlinedButtonOnClick,
    refs
  } = useItemView<Outlet>({
    // mirror Vendors fields, adding bio
    fields: [
      "name",
      "slug",
      "url",
      "domain",
      "logoImageId",
      "splashImageId",
      "bio"
    ],
    isAdd,
    isEditing,
    isSaveLoading,
    item,
    onCancel,
    onDelete,
    onEdit,
    onPublishToggle
  })

  const refsMap = refs as {
    bio: RefObject<HTMLTextAreaElement | null>
    domain: RefObject<HTMLInputElement | null>
    logoImageId: RefObject<HTMLInputElement | null>
    name: RefObject<HTMLInputElement | null>
    slug: RefObject<HTMLInputElement | null>
    splashImageId: RefObject<HTMLInputElement | null>
    url: RefObject<HTMLInputElement | null>
  }

  const {
    bio: bioRef,
    domain: domainRef,
    logoImageId: logoImageIdRef,
    name: nameRef,
    slug: slugRef,
    splashImageId: splashImageIdRef,
    url: urlRef
  } = refsMap

  const onGenerateSlug = useCallback(() => {
    if (!nameRef.current || !slugRef.current) {
      return
    }

    const newSlug = slugify(nameRef.current.value || "")

    slugRef.current.value = newSlug
    slugRef.current.dispatchEvent(new Event("input", { bubbles: true }))
  }, [nameRef, slugRef])

  const onSubmit: FormEventHandler = useCallback(
    (event) => {
      event.preventDefault()
      if (!nameRef.current?.value || !slugRef.current?.value) {
        return
      }

      const outletUpdateInput: OutletUpdateInput = {
        bio: bioRef.current?.value || undefined,
        domain: domainRef.current?.value,
        logoImageId: logoImageIdRef.current?.value
          ? Number(logoImageIdRef.current.value)
          : undefined,
        name: nameRef.current.value,
        slug: slugRef.current.value,
        splashImageId: splashImageIdRef.current?.value
          ? Number(splashImageIdRef.current.value)
          : undefined,
        url: urlRef.current?.value
      }

      onSave(outletUpdateInput)
    },
    [
      bioRef,
      domainRef,
      logoImageIdRef,
      nameRef,
      onSave,
      slugRef,
      splashImageIdRef,
      urlRef
    ]
  )

  return {
    ...props,
    actionButtonLabel,
    actionButtonOnClick,
    bioRef,
    domainRef,
    isActionButtonLoading,
    isAdd,
    isEditMode,
    isModalOpen,
    item,
    logoImageIdRef,
    modalContent,
    nameRef,
    onGenerateSlug,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    onSubmit,
    outlinedButtonLabel,
    outlinedButtonOnClick,
    slugRef,
    splashImageIdRef,
    urlRef
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Item
)
